/**
 * HerSkin — Web Interface for Hermes Agent
 * @author Jevin With GPT
 * @since 2026-08
 */

import {
  AuthSessionSchema,
  ConnectionStateSchema,
  HermesProfileCatalogSchema,
  MessageSchema,
  NotificationListSchema,
  ProfileSettingsSchema,
  RunControlStateSchema,
  SessionPageSchema,
  SessionMutationResponseSchema,
  SessionRuntimeStateSchema,
  SessionSnapshotSchema,
  SessionSummarySchema,
  TaskPushFeedSchema,
  type AgentEvent,
  type AuthSession,
  type ConnectionState,
  type HermesProfileCatalog,
  type NotificationItem,
  type NotificationList,
  type ProfileSettings,
  type ApprovalChoice,
  type RunAction,
  type RunControlState,
  type RunRecoveryResponse,
  type RunStatus,
  type SessionSummary,
  type SessionRuntimeState,
  type SessionSnapshot,
} from "@herskin/contracts";
import { IconButton, InlineError, StatusBadge } from "@herskin/ui";
import { IconChevronLeft as ChevronLeft, IconSparkles as Sparkles } from "@tabler/icons-react";
import {
  Suspense,
  type FormEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api, ApiError } from "./api";
import { AttachmentChips, AttachmentPicker } from "./AttachmentControls";
import {
  chipFromPrepared,
  discardPreparedAttachment,
  lockAttachmentsAfterSubmitFailure,
  MAX_ATTACHMENTS_PER_PROMPT,
  preflightAttachment,
  uploadPreparedAttachment,
  type AttachmentChip,
  type AttachmentPreflight,
} from "./attachments";
import { AutoGrowTextarea } from "./AutoGrowTextarea";
import { stageClipboardImages } from "./clipboard";
import { ComposerSendButton } from "./ComposerSendButton";
import { ModelSelector } from "./ModelSelector";
import {
  INITIAL_MESSAGE_WINDOW,
  nextMessageWindowLimit,
  sliceMessageWindow,
} from "./messageWindow";
import { type BrowserNotificationState } from "./NotificationCenter";
import { buildMessagePresentations, isActivelyRunning, runTone, statusLabel } from "./presentation";
import { SessionIdentity } from "./SessionIdentity";
import { loadProfile, PROFILE_UPDATED_EVENT } from "./profile";
import {
  applySessionMutation,
  applySessionUpdatedEvent,
  mutationNotice,
} from "./sessionManagement";
import { RunRecoveryCard, type RunRecoveryAction } from "./RunRecoveryCard";
import { RunActionCards } from "./RunInspector";
import {
  promptBlockedHint,
  reconcileRun,
  recoveryResultMessage,
  resolveRun,
  runBlocksNewPrompt,
} from "./runRecovery";
import { compactContextLabel, runtimeAvailabilityCopy } from "./runtimePresentation";
import { useRealtime } from "./useRealtime";
import { bindVisualViewportLayout } from "./visualViewportLayout";
import { WorkspaceDock, type WorkspaceId } from "./WorkspaceDock";
import { TaskPushFeed } from "./TaskPushFeed";
import { HERMES_PROFILES_UPDATED_EVENT, loadHermesProfiles } from "./hermesProfiles";
import { type ProfileScope } from "./ProfileSwitcher";
import { LoginScreen } from "./LoginScreen";
import { MessageItem, RunPlaceholder } from "./ConversationMessage";
import { AppTopbar } from "./AppTopbar";
import { ChatInspector } from "./ChatInspector";
import { SessionRail } from "./SessionRail";
import { WorkspaceSkeleton } from "./WorkspaceSkeleton";
import {
  CronWorkspace,
  FileWorkspace,
  ManagementWorkspace,
  MemoryWorkspace,
  preloadWorkspace,
} from "./workspaceModules";

interface SessionFeedback {
  tone: "success" | "warning" | "danger";
  message: string;
}

export function App() {
  const [auth, setAuth] = useState<AuthSession | null>(null);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<SessionSnapshot | null>(null);
  const [snapshotLoadError, setSnapshotLoadError] = useState("");
  const [runtimeState, setRuntimeState] = useState<SessionRuntimeState | null>(null);
  const [runtimeLoading, setRuntimeLoading] = useState(false);
  const [runControl, setRunControl] = useState<RunControlState | null>(null);
  const [runControlBusy, setRunControlBusy] = useState<string | null>(null);
  const [runControlError, setRunControlError] = useState("");
  const [connection, setConnection] = useState<ConnectionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mobileChatDetail, setMobileChatDetail] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [activeWorkspace, setActiveWorkspace] = useState<WorkspaceId>("chat");
  const [taskPushOpen, setTaskPushOpen] = useState(false);
  const [taskPushUnread, setTaskPushUnread] = useState(0);
  const [profile, setProfile] = useState<ProfileSettings | null>(null);
  const [hermesProfiles, setHermesProfiles] = useState<HermesProfileCatalog | null>(null);
  const [profileScope, setProfileScope] = useState<ProfileScope>("default");
  const [profilePickerRequest, setProfilePickerRequest] = useState(0);
  const [pendingProfileForNewSession, setPendingProfileForNewSession] = useState(false);
  const [sessionSearchOpen, setSessionSearchOpen] = useState(false);
  const [dockCollapsed, setDockCollapsed] = useState(false);
  const [memoryDirty, setMemoryDirty] = useState(false);
  const [notifications, setNotifications] = useState<NotificationList | null>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [browserNotificationState, setBrowserNotificationState] =
    useState<BrowserNotificationState>(() =>
      typeof Notification === "undefined" ? "unsupported" : Notification.permission,
    );
  const [cronFocusId, setCronFocusId] = useState<string | null>(null);
  const [moreTarget, setMoreTarget] = useState<{
    component: "hermes_serve" | "hermes_gateway" | "controller";
    page?: "connection" | "profiles";
    nonce: number;
  } | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [busySessionId, setBusySessionId] = useState<string | null>(null);
  const [sessionFeedback, setSessionFeedback] = useState<SessionFeedback | null>(null);
  const [runRecoveryResult, setRunRecoveryResult] = useState<RunRecoveryResponse | null>(null);
  const [runRecoveryAction, setRunRecoveryAction] = useState<RunRecoveryAction>(null);
  const [runRecoveryNotice, setRunRecoveryNotice] = useState<SessionFeedback | null>(null);
  const [attachmentsBySession, setAttachmentsBySession] = useState<
    Record<string, AttachmentChip[]>
  >({});
  const [visibleMessageLimit, setVisibleMessageLimit] = useState(INITIAL_MESSAGE_WINDOW);
  const messageScroll = useRef<HTMLElement>(null);
  const prependScrollAnchor = useRef<number | null>(null);
  const shouldAutoScroll = useRef(true);
  const autoScrollFrame = useRef<number | null>(null);
  const appShellRef = useRef<HTMLDivElement>(null);
  const selectedIdRef = useRef<string | null>(null);
  const draftRef = useRef("");
  const draftPersistTimer = useRef<number | null>(null);
  draftRef.current = draft;

  const persistDraftNow = useCallback(() => {
    if (draftPersistTimer.current !== null) window.clearTimeout(draftPersistTimer.current);
    draftPersistTimer.current = null;
    const sessionId = selectedIdRef.current;
    if (sessionId) localStorage.setItem(`herskin:draft:${sessionId}`, draftRef.current);
  }, []);
  const sessionsRef = useRef<SessionSummary[]>([]);
  const notificationBaselineReady = useRef(false);
  const openedNotificationRef = useRef<(item: NotificationItem) => void>(() => undefined);

  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  useEffect(() => {
    if (!appShellRef.current) return;
    return bindVisualViewportLayout(appShellRef.current);
  }, [auth?.authenticated]);

  const notifyBrowser = useCallback((item: NotificationItem) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const storageKey = "herskin:browser-notification-events";
    const dedupeId = item.sourceEventId ?? item.notificationId;
    let seen: string[] = [];
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(storageKey) ?? "[]");
      if (Array.isArray(parsed))
        seen = parsed.filter((value): value is string => typeof value === "string");
    } catch {
      // 损坏的本地 UI 偏好直接重建；服务端通知仍是权威来源。
    }
    if (seen.includes(dedupeId)) return;
    localStorage.setItem(storageKey, JSON.stringify([...seen.slice(-199), dedupeId]));
    const notice = new Notification(item.title, { body: item.detail, tag: dedupeId });
    notice.onclick = () => {
      window.focus();
      openedNotificationRef.current(item);
      notice.close();
    };
  }, []);

  const loadNotifications = useCallback(
    async (showNewBrowserNotifications = false) => {
      const next = NotificationListSchema.parse(await api<unknown>("/api/notifications?limit=50"));
      setNotifications((current) => {
        if (showNewBrowserNotifications && notificationBaselineReady.current) {
          const known = new Set(current?.items.map((item) => item.notificationId) ?? []);
          for (const item of next.items) {
            if (!item.readAt && !known.has(item.notificationId)) notifyBrowser(item);
          }
        }
        return next;
      });
      notificationBaselineReady.current = true;
      return next;
    },
    [notifyBrowser],
  );

  const selectSession = useCallback(
    (sessionId: string | null) => {
      setTaskPushOpen(false);
      if (selectedIdRef.current && selectedIdRef.current !== sessionId) persistDraftNow();
      // 点击切换时先更新 ref，避免上一个 Session 的尾部事件在 React 提交前串入新视图。
      if (selectedIdRef.current !== sessionId) {
        setSnapshot(null);
        setRuntimeState(null);
        setRunControl(null);
      }
      selectedIdRef.current = sessionId;
      setSelectedId(sessionId);
      setMobileChatDetail(Boolean(sessionId));
      if (sessionId) {
        setSessions((current) =>
          current.map((item) =>
            item.id === sessionId ? { ...item, unreadCount: 0, attention: "none" } : item,
          ),
        );
      }
    },
    [persistDraftNow],
  );

  const openNotification = useCallback(
    (item: NotificationItem) => {
      if (
        activeWorkspace === "memory" &&
        memoryDirty &&
        !window.confirm("记忆文档还有未保存草稿。离开后草稿会丢失，是否继续？")
      ) {
        return;
      }
      setNotificationsOpen(false);
      setNotifications((current) =>
        current
          ? {
              ...current,
              unreadCount: Math.max(0, current.unreadCount - (item.readAt ? 0 : 1)),
              items: current.items.map((candidate) =>
                candidate.notificationId === item.notificationId
                  ? { ...candidate, readAt: candidate.readAt ?? new Date().toISOString() }
                  : candidate,
              ),
            }
          : current,
      );
      void api(`/api/notifications/${encodeURIComponent(item.notificationId)}/read`, {
        method: "POST",
      }).catch(() => void loadNotifications());

      if (item.target.workspace === "chat") {
        setActiveWorkspace("chat");
        selectSession(item.target.sessionId);
        if (item.kind === "action_required") setInspectorOpen(true);
      } else {
        setCronFocusId(item.target.jobId);
        setActiveWorkspace("cron");
      }
    },
    [activeWorkspace, loadNotifications, memoryDirty, selectSession],
  );
  openedNotificationRef.current = openNotification;

  const markAllNotificationsRead = useCallback(async () => {
    const readAt = new Date().toISOString();
    setNotifications((current) =>
      current
        ? {
            ...current,
            unreadCount: 0,
            items: current.items.map((item) => ({ ...item, readAt: item.readAt ?? readAt })),
          }
        : current,
    );
    try {
      await api("/api/notifications/read-all", { method: "POST" });
    } catch {
      await loadNotifications().catch(() => undefined);
    }
  }, [loadNotifications]);

  const toggleNotifications = useCallback(() => {
    if (!notificationsOpen && notifications?.unreadCount) void markAllNotificationsRead();
    setNotificationsOpen((value) => !value);
  }, [markAllNotificationsRead, notifications?.unreadCount, notificationsOpen]);

  const enableBrowserNotifications = useCallback(async () => {
    if (typeof Notification === "undefined") {
      setBrowserNotificationState("unsupported");
      return;
    }
    // 浏览器权限只能由老板在明确按钮上触发；页面加载和实时事件绝不自动弹权限框。
    const permission = await Notification.requestPermission();
    setBrowserNotificationState(permission);
  }, []);

  const loadSessions = useCallback(async () => {
    const body = SessionPageSchema.parse(await api<unknown>("/api/sessions?view=all"));
    setSessions(body.items);
    setSelectedId((current) => {
      const currentStillExists = current && body.items.some((item) => item.id === current);
      const firstActive = body.items.find(
        (item) => (item.management?.lifecycle ?? "active") === "active",
      );
      const next = currentStillExists ? current : (firstActive?.id ?? body.items[0]?.id ?? null);
      selectedIdRef.current = next;
      return next;
    });
    return body.items;
  }, []);

  const loadSnapshot = useCallback(
    async (sessionId = selectedId) => {
      if (!sessionId) {
        setSnapshot(null);
        return;
      }
      const body = SessionSnapshotSchema.parse(
        await api<unknown>(`/api/sessions/${encodeURIComponent(sessionId)}`),
      );
      // 切换会话后，较慢的旧请求只能更新自己的列表摘要，绝不能覆盖当前会话正文。
      if (selectedIdRef.current === sessionId) setSnapshot(body);
      setSessions((current) =>
        current.map((item) =>
          item.id === sessionId && body.session.updatedAt >= item.updatedAt ? body.session : item,
        ),
      );
    },
    [selectedId],
  );

  const loadRuntimeState = useCallback(
    async (sessionId = selectedId) => {
      if (!sessionId) {
        setRuntimeState(null);
        setRuntimeLoading(false);
        return;
      }
      if (selectedIdRef.current === sessionId) setRuntimeLoading(true);
      try {
        const body = SessionRuntimeStateSchema.parse(
          await api<unknown>(`/api/sessions/${encodeURIComponent(sessionId)}/runtime`),
        );
        if (selectedIdRef.current === sessionId) setRuntimeState(body);
      } catch {
        // 运行用量是增强信息，失败不能遮挡聊天正文；界面保持明确“暂不可用”。
        if (selectedIdRef.current === sessionId) setRuntimeState(null);
      } finally {
        if (selectedIdRef.current === sessionId) setRuntimeLoading(false);
      }
    },
    [selectedId],
  );

  const loadRunControl = useCallback(
    async (sessionId = selectedId) => {
      if (!sessionId) {
        setRunControl(null);
        return;
      }
      try {
        const body = RunControlStateSchema.parse(
          await api<unknown>(`/api/sessions/${encodeURIComponent(sessionId)}/run-control`),
        );
        if (selectedIdRef.current === sessionId) {
          setRunControl(body);
          // 待审批/待补充不是普通状态提示，而是会阻塞 Hermes 的可操作请求。
          // 即使用户此前收起检查器，刷新或新事件到达时也必须把入口带回可视区。
          if (body.actions.some((action) => action.status === "pending")) setInspectorOpen(true);
        }
      } catch {
        if (selectedIdRef.current === sessionId) setRunControl(null);
      }
    },
    [selectedId],
  );

  useEffect(() => {
    void (async () => {
      try {
        setAuth(AuthSessionSchema.parse(await api<unknown>("/api/auth/me")));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!auth?.authenticated) return;
    void Promise.all([
      loadSessions(),
      loadNotifications(),
      api<unknown>("/api/task-push?limit=1").then((value) =>
        setTaskPushUnread(TaskPushFeedSchema.parse(value).unreadCount),
      ),
      loadProfile().then((value) => setProfile(ProfileSettingsSchema.parse(value))),
      loadHermesProfiles().then((value) => {
        const catalog = HermesProfileCatalogSchema.parse(value);
        setHermesProfiles(catalog);
        setProfileScope((current) => {
          const stored = localStorage.getItem("herskin:profile-scope");
          const candidate = stored || current;
          return (candidate === "all" && catalog.items.length > 1) ||
            catalog.items.some((item) => item.name === candidate)
            ? candidate
            : (catalog.items.find((item) => item.isDefault)?.name ?? catalog.items[0]!.name);
        });
      }),
      api<unknown>("/api/status").then((body) => setConnection(ConnectionStateSchema.parse(body))),
    ]).catch((caught) => setError(caught instanceof Error ? caught.message : "加载失败"));
  }, [auth, loadNotifications, loadSessions]);

  useEffect(() => {
    if (!auth?.authenticated) return;
    const refresh = () =>
      void loadProfile()
        .then(setProfile)
        .catch(() => undefined);
    window.addEventListener(PROFILE_UPDATED_EVENT, refresh);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, refresh);
  }, [auth]);

  useEffect(() => {
    if (!auth?.authenticated) return;
    const refresh = () =>
      void loadHermesProfiles()
        .then((catalog) => {
          setHermesProfiles(catalog);
          setProfileScope((current) =>
            (current === "all" && catalog.items.length > 1) ||
            catalog.items.some((item) => item.name === current)
              ? current
              : (catalog.items.find((item) => item.isDefault)?.name ?? catalog.items[0]!.name),
          );
        })
        .catch(() => undefined);
    window.addEventListener(HERMES_PROFILES_UPDATED_EVENT, refresh);
    return () => window.removeEventListener(HERMES_PROFILES_UPDATED_EVENT, refresh);
  }, [auth]);

  useEffect(() => {
    if (!auth?.authenticated || !hermesProfiles) return;
    // 当 Profile 被删除、目录刷新或从单 Profile 升级到多 Profile 时，持久化
    // 已经校正过的作用域，避免下次加载反复命中失效的旧选择。
    localStorage.setItem("herskin:profile-scope", profileScope);
  }, [auth, hermesProfiles, profileScope]);

  useEffect(() => {
    if (!auth?.authenticated) return;
    const timer = window.setInterval(() => {
      void loadNotifications(true);
      void api<unknown>("/api/task-push?limit=1")
        .then((value) => setTaskPushUnread(TaskPushFeedSchema.parse(value).unreadCount))
        .catch(() => undefined);
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [auth, loadNotifications]);

  useEffect(() => {
    if (!selectedId) return;
    selectedIdRef.current = selectedId;
    shouldAutoScroll.current = true;
    setDraft(localStorage.getItem(`herskin:draft:${selectedId}`) ?? "");
    // 切换会话时不保留上一会话正文；加载错误也只属于发起请求的当前 Session。
    setSnapshot(null);
    setRuntimeState(null);
    setRunControl(null);
    setRunControlError("");
    setRuntimeLoading(true);
    setSnapshotLoadError("");
    setError("");
    setVisibleMessageLimit(INITIAL_MESSAGE_WINDOW);
    prependScrollAnchor.current = null;
    setRunRecoveryResult(null);
    setRunRecoveryAction(null);
    setRunRecoveryNotice(null);
    void loadSnapshot(selectedId)
      .then(() => {
        if (selectedIdRef.current === selectedId) setSnapshotLoadError("");
        return Promise.all([loadRuntimeState(selectedId), loadRunControl(selectedId)]);
      })
      .catch((caught) => {
        if (selectedIdRef.current !== selectedId) return;
        setRuntimeLoading(false);
        setSnapshotLoadError(
          caught instanceof ApiError
            ? `${caught.message}（错误 ID：${caught.errorId}）`
            : "会话数据格式异常，暂时无法显示内容；请刷新后重试",
        );
      });
  }, [selectedId, loadSnapshot, loadRuntimeState, loadRunControl]);

  useEffect(() => {
    if (!selectedId) return;
    if (draftPersistTimer.current !== null) window.clearTimeout(draftPersistTimer.current);
    // localStorage 是同步 API；输入期间延迟落盘，避免长草稿每个按键都阻塞主线程。
    draftPersistTimer.current = window.setTimeout(persistDraftNow, 300);
    return () => {
      if (draftPersistTimer.current !== null) window.clearTimeout(draftPersistTimer.current);
      draftPersistTimer.current = null;
    };
  }, [draft, persistDraftNow, selectedId]);

  useEffect(() => () => persistDraftNow(), [persistDraftNow]);

  const onRealtimeEvent = useCallback(
    (event: AgentEvent) => {
      if (event.type === "connection.state_changed") {
        void api<unknown>("/api/status")
          .then((body) => setConnection(ConnectionStateSchema.parse(body)))
          .catch(() => undefined);
        const visibleSessionId = selectedIdRef.current;
        if (visibleSessionId) void loadRuntimeState(visibleSessionId);
        return;
      }
      if (!event.sessionId) return;
      const visibleSessionId = selectedIdRef.current;
      if (event.type === "session.updated") {
        const parsedSession = SessionSummarySchema.safeParse(event.payload.session);
        const incoming = parsedSession.success ? parsedSession.data : null;
        const action = event.payload.action;
        setSessions((current) =>
          applySessionUpdatedEvent(current, event.sessionId!, action, incoming),
        );

        const removedFromActiveList =
          action === "deleted" ||
          incoming?.management?.lifecycle === "archived" ||
          incoming?.management?.lifecycle === "deleted";
        if (event.sessionId === visibleSessionId && removedFromActiveList) {
          persistDraftNow();
          const nextId =
            sessionsRef.current.find(
              (candidate) =>
                candidate.id !== event.sessionId &&
                (candidate.management?.lifecycle ?? "active") === "active",
            )?.id ?? null;
          selectedIdRef.current = nextId;
          setSelectedId(nextId);
          setSnapshot(null);
        } else if (event.sessionId === visibleSessionId && incoming) {
          setSnapshot((current) => (current ? { ...current, session: incoming } : current));
        }
        if (action === "deleted") {
          setAttachmentsBySession((current) => {
            const next = { ...current };
            delete next[event.sessionId!];
            return next;
          });
        }
        return;
      }
      // 高频 token 增量只更新正文。会话列表在运行阶段事件和完成事件到达时更新，
      // 避免每个 token 都重排、重绘整条侧栏。
      if (event.type !== "message.delta") {
        const parsedSession = SessionSummarySchema.safeParse(event.payload.session);
        if (parsedSession.success) {
          setSessions((current) => [
            parsedSession.data,
            ...current.filter((item) => item.id !== parsedSession.data.id),
          ]);
        }
        setSessions((current) =>
          current.map((item) => {
            if (item.id !== event.sessionId) return item;
            const eventStatus =
              typeof event.payload.status === "string"
                ? (event.payload.status as RunStatus)
                : item.activeRunStatus;
            const becomesUnread =
              event.sessionId !== visibleSessionId && event.type === "message.completed";
            return {
              ...item,
              activeRunStatus:
                event.type === "run.completed" || event.type === "run.failed" ? null : eventStatus,
              unreadCount: becomesUnread ? item.unreadCount + 1 : item.unreadCount,
              attention: becomesUnread ? "unread" : item.attention,
              updatedAt: event.receivedAt,
            };
          }),
        );
      }
      if (
        event.type === "run.completed" ||
        event.type === "run.failed" ||
        event.type === "run.action_updated"
      ) {
        void loadNotifications(true);
      }
      if (event.sessionId !== visibleSessionId) return;
      if (
        event.type === "run.activity_updated" ||
        event.type === "run.action_updated" ||
        event.type === "run.status_changed" ||
        event.type === "run.completed" ||
        event.type === "run.failed"
      ) {
        if (event.type === "run.action_updated") setInspectorOpen(true);
        void loadRunControl(event.sessionId);
      }
      if (event.type === "run.completed" || event.type === "run.failed") {
        void loadRuntimeState(event.sessionId);
      }
      setSnapshot((current) => {
        if (!current) return current;
        if (event.type === "run.accepted") {
          // replay 可能包含升级附件协议前持久化的旧 userMessage。通过当前 Schema 解析既能
          // 补上 attachments 默认值，也会拒绝真正损坏的事件，避免直接 cast 后击穿渲染。
          const parsedUserMessage = MessageSchema.safeParse(event.payload.userMessage);
          const userMessage = parsedUserMessage.success ? parsedUserMessage.data : undefined;
          if (!userMessage || current.messages.some((message) => message.id === userMessage.id))
            return current;
          return { ...current, messages: [...current.messages, userMessage] };
        }
        if (event.type === "message.started") {
          const messageId = String(event.payload.messageId ?? "");
          if (!messageId || current.messages.some((message) => message.id === messageId))
            return current;
          const now = new Date().toISOString();
          return {
            ...current,
            messages: [
              ...current.messages,
              {
                id: messageId,
                sessionId: event.sessionId!,
                runId: event.runId ?? null,
                role: "assistant",
                format: "markdown",
                status: "streaming",
                content: "",
                attachments: [],
                createdAt: now,
                updatedAt: now,
              },
            ],
          };
        }
        if (event.type === "message.delta") {
          const messageId = String(event.payload.messageId ?? "");
          const delta = String(event.payload.delta ?? "");
          return {
            ...current,
            messages: current.messages.map((message) =>
              message.id === messageId
                ? {
                    ...message,
                    content: message.content + delta,
                    status: "streaming",
                    updatedAt: event.receivedAt,
                  }
                : message,
            ),
          };
        }
        if (event.type === "message.completed") {
          const messageId = String(event.payload.messageId ?? "");
          const content = String(event.payload.content ?? "");
          return {
            ...current,
            activeRun: null,
            messages: current.messages.map((message) =>
              message.id === messageId
                ? { ...message, content, status: "completed", updatedAt: event.receivedAt }
                : message,
            ),
          };
        }
        return current;
      });
    },
    [loadNotifications, loadRunControl, loadRuntimeState, persistDraftNow],
  );

  const realtimeState = useRealtime(
    Boolean(auth?.authenticated),
    selectedId,
    onRealtimeEvent,
    () => void loadSnapshot().catch(() => undefined),
  );

  useEffect(() => {
    if (!shouldAutoScroll.current) return;
    if (autoScrollFrame.current !== null) cancelAnimationFrame(autoScrollFrame.current);
    autoScrollFrame.current = requestAnimationFrame(() => {
      const scroller = messageScroll.current;
      if (scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior: "auto" });
      autoScrollFrame.current = null;
    });
    return () => {
      if (autoScrollFrame.current !== null) cancelAnimationFrame(autoScrollFrame.current);
    };
  }, [snapshot?.messages, runControl?.actions]);

  const currentSession = useMemo(
    () => sessions.find((session) => session.id === selectedId) ?? null,
    [sessions, selectedId],
  );
  const scopedSessions = useMemo(
    () =>
      profileScope === "all"
        ? sessions
        : sessions.filter((session) => session.profileName === profileScope),
    [profileScope, sessions],
  );
  const currentAgentName =
    currentSession?.profileName === "default"
      ? (profile?.agent.displayName ?? "Hermes")
      : (currentSession?.profileName ?? profile?.agent.displayName ?? "Hermes");
  const displayedRunStatus = currentSession?.activeRunStatus ?? snapshot?.activeRun?.status ?? null;
  const messages = snapshot?.messages ?? [];
  const hasStreamingAssistant = messages.some(
    (message) => message.role === "assistant" && message.status === "streaming",
  );
  const lastMessage = messages[messages.length - 1];
  const justCompletedWithoutActiveRun =
    !snapshot?.activeRun && lastMessage?.role === "assistant" && lastMessage.status !== "streaming";
  const showRunPlaceholder =
    isActivelyRunning(displayedRunStatus) &&
    !hasStreamingAssistant &&
    (!justCompletedWithoutActiveRun || sending);
  const messagePresentations = useMemo(
    () => buildMessagePresentations(messages, snapshot?.activeRun ?? null),
    [messages, snapshot?.activeRun],
  );
  const messageWindow = useMemo(
    () => sliceMessageWindow(messagePresentations, visibleMessageLimit),
    [messagePresentations, visibleMessageLimit],
  );

  useLayoutEffect(() => {
    const distanceFromBottom = prependScrollAnchor.current;
    const scroller = messageScroll.current;
    if (distanceFromBottom === null || !scroller) return;
    scroller.scrollTop = Math.max(0, scroller.scrollHeight - distanceFromBottom);
    prependScrollAnchor.current = null;
  }, [visibleMessageLimit]);

  const showEarlierMessages = useCallback(() => {
    const scroller = messageScroll.current;
    if (scroller) {
      prependScrollAnchor.current = scroller.scrollHeight - scroller.scrollTop;
      shouldAutoScroll.current = false;
    }
    setVisibleMessageLimit((current) =>
      nextMessageWindowLimit(current, messagePresentations.length),
    );
  }, [messagePresentations.length]);
  const currentSessionArchived = currentSession?.management?.lifecycle === "archived";
  const currentAttachments = selectedId ? (attachmentsBySession[selectedId] ?? []) : [];
  const attachmentsReady = currentAttachments.every(
    (attachment) => attachment.status === "ready" && Boolean(attachment.attachmentId),
  );
  const promptBlockedByRun = runBlocksNewPrompt(displayedRunStatus);
  const currentPromptBlockedHint = promptBlockedHint(displayedRunStatus);
  const unknownRunId = displayedRunStatus === "unknown" ? (snapshot?.activeRun?.id ?? null) : null;
  const contextLabel = compactContextLabel(runtimeState, runtimeLoading);
  const contextCopy = runtimeAvailabilityCopy(runtimeState, runtimeLoading);
  const contextUsage = runtimeState?.usage ?? null;
  const mobileContextLabel =
    contextUsage?.occupancyMeasurement === "live_window" && contextUsage.contextPercent !== null
      ? `${Math.round(contextUsage.contextPercent)}%`
      : contextLabel.replace("当前窗口", "");

  const updateSessionAttachments = useCallback(
    (sessionId: string, update: (attachments: AttachmentChip[]) => AttachmentChip[]) => {
      setAttachmentsBySession((current) => ({
        ...current,
        [sessionId]: update(current[sessionId] ?? []),
      }));
    },
    [],
  );

  const uploadAttachment = async (
    file: File,
    sessionId: string,
    clientId: string,
    preflight: AttachmentPreflight,
  ) => {
    try {
      const prepared = await uploadPreparedAttachment(file, sessionId, preflight);
      updateSessionAttachments(sessionId, (current) =>
        current.map((attachment) =>
          attachment.clientId === clientId ? chipFromPrepared(clientId, prepared) : attachment,
        ),
      );
    } catch (caught) {
      const message =
        caught instanceof ApiError
          ? `${caught.message}（错误 ID：${caught.errorId}）`
          : caught instanceof Error
            ? caught.message
            : "上传失败";
      updateSessionAttachments(sessionId, (current) =>
        current.map((attachment) =>
          attachment.clientId === clientId
            ? { ...attachment, status: "failed", error: message }
            : attachment,
        ),
      );
    }
  };

  const selectAttachments = (files: File[]) => {
    if (!files.length || !selectedId || currentSessionArchived || sending || promptBlockedByRun)
      return false;
    setError("");
    const available = MAX_ATTACHMENTS_PER_PROMPT - currentAttachments.length;
    if (available <= 0) {
      setError("每条消息最多添加 5 个附件");
      return false;
    }
    if (files.length > available)
      setError(`每条消息最多添加 5 个附件，本次只处理前 ${available} 个`);

    const selected = files.slice(0, available).map((file) => {
      const result = preflightAttachment(file);
      const clientId = `local_attachment_${crypto.randomUUID()}`;
      const chip: AttachmentChip =
        "error" in result
          ? {
              clientId,
              sessionId: selectedId,
              filename: file.name,
              mimeType: file.type || "application/octet-stream",
              kind: null,
              sizeBytes: file.size,
              status: "failed",
              error: result.error,
            }
          : {
              clientId,
              sessionId: selectedId,
              filename: file.name,
              mimeType: result.mimeType,
              kind: result.kind,
              sizeBytes: file.size,
              status: "uploading",
            };
      return { file, result, chip };
    });

    updateSessionAttachments(selectedId, (current) => [
      ...current,
      ...selected.map(({ chip }) => chip),
    ]);
    // 依次上传，避免一次选择 5 个大文件时同时复制最多 100 MiB 到内存。
    void (async () => {
      for (const item of selected) {
        if (!("error" in item.result)) {
          await uploadAttachment(item.file, selectedId, item.chip.clientId, item.result);
        }
      }
    })();
    return selected.length > 0;
  };

  const removeAttachment = async (attachment: AttachmentChip) => {
    const sessionId = attachment.sessionId;
    if (!attachment.attachmentId) {
      updateSessionAttachments(sessionId, (current) =>
        current.filter((item) => item.clientId !== attachment.clientId),
      );
      return;
    }
    updateSessionAttachments(sessionId, (current) =>
      current.map((item) => {
        if (item.clientId !== attachment.clientId) return item;
        return { ...item, status: "removing" };
      }),
    );
    try {
      await discardPreparedAttachment(attachment.attachmentId, sessionId);
      updateSessionAttachments(sessionId, (current) =>
        current.filter((item) => item.clientId !== attachment.clientId),
      );
    } catch (caught) {
      const message =
        caught instanceof ApiError
          ? `${caught.message}（错误 ID：${caught.errorId}）`
          : caught instanceof Error
            ? caught.message
            : "移除失败";
      // 未确认删除成功时保留 attachmentId，只有老板再次点击才会重试 DELETE。
      updateSessionAttachments(sessionId, (current) =>
        current.map((item) =>
          item.clientId === attachment.clientId
            ? { ...item, status: "failed", error: `移除失败：${message}` }
            : item,
        ),
      );
    }
  };

  const createSession = async (requestedProfile = profileScope) => {
    if (requestedProfile === "all") {
      setPendingProfileForNewSession(true);
      setProfilePickerRequest(Date.now());
      return;
    }
    const sessionId = `ses_${crypto.randomUUID()}`;
    const created = SessionSummarySchema.parse(
      await api<unknown>("/api/sessions", {
        method: "POST",
        body: JSON.stringify({
          sessionId,
          title: "新会话",
          profileName: requestedProfile,
        }),
      }),
    );
    // REST 响应与实时 session.created 可能相邻到达；按稳定 ID 合并而不是重复插入。
    setSessions((current) => [created, ...current.filter((item) => item.id !== created.id)]);
    setProfileScope(requestedProfile);
    localStorage.setItem("herskin:profile-scope", requestedProfile);
    selectSession(created.id);
  };

  const selectProfileScope = (nextScope: ProfileScope) => {
    persistDraftNow();
    setTaskPushOpen(false);
    setProfileScope(nextScope);
    localStorage.setItem("herskin:profile-scope", nextScope);
    if (nextScope !== "all") {
      const currentMatches = sessions.some(
        (item) => item.id === selectedIdRef.current && item.profileName === nextScope,
      );
      if (!currentMatches) {
        const first = sessions.find((item) => item.profileName === nextScope);
        if (first) selectSession(first.id);
        else {
          selectedIdRef.current = null;
          setSelectedId(null);
          setSnapshot(null);
        }
      }
      if (pendingProfileForNewSession) {
        setPendingProfileForNewSession(false);
        void createSession(nextScope);
      }
    }
  };

  const mutateSession = useCallback(
    async (
      session: SessionSummary,
      pathname: string,
      method: "PATCH" | "POST" | "DELETE",
      extraBody: Record<string, unknown> = {},
    ) => {
      const baseRevision = session.management?.revision;
      if (baseRevision === undefined) {
        setSessionFeedback({ tone: "danger", message: "会话版本信息尚未就绪，请稍后重试" });
        void loadSessions().catch(() => undefined);
        return false;
      }
      if (busySessionId) return false;

      setBusySessionId(session.id);
      setSessionFeedback(null);
      try {
        const mutation = SessionMutationResponseSchema.parse(
          await api<unknown>(pathname, {
            method,
            body: JSON.stringify({ baseRevision, ...extraBody }),
          }),
        );
        setSessions((current) => applySessionMutation(current, session.id, mutation));
        if (mutation.action === "deleted") {
          setAttachmentsBySession((current) => {
            const next = { ...current };
            delete next[session.id];
            return next;
          });
        }
        if (mutation.session && selectedIdRef.current === session.id) {
          setSnapshot((current) =>
            current ? { ...current, session: mutation.session! } : current,
          );
        }
        if (
          (mutation.action === "archived" || mutation.action === "deleted") &&
          selectedIdRef.current === session.id
        ) {
          const next = sessionsRef.current.find(
            (candidate) =>
              candidate.id !== session.id &&
              (candidate.management?.lifecycle ?? "active") === "active",
          );
          selectSession(next?.id ?? null);
        }
        setSessionFeedback(mutationNotice(mutation));
        return true;
      } catch (caught) {
        const message =
          caught instanceof ApiError
            ? `${caught.message}（错误 ID：${caught.errorId}）`
            : caught instanceof Error
              ? caught.message
              : "会话操作失败";
        // 409/REVISION_CONFLICT 的服务端原始消息直接呈现；版本冲突后只刷新列表，不盲目重试副作用。
        setSessionFeedback({ tone: "danger", message });
        if (caught instanceof ApiError && caught.code === "REVISION_CONFLICT") {
          void loadSessions().catch(() => undefined);
        }
        return false;
      } finally {
        setBusySessionId(null);
      }
    },
    [busySessionId, loadSessions, selectSession],
  );

  const renameSession = useCallback(
    (session: SessionSummary, title: string) =>
      mutateSession(session, `/api/sessions/${encodeURIComponent(session.id)}`, "PATCH", { title }),
    [mutateSession],
  );

  const archiveSession = useCallback(
    (session: SessionSummary) =>
      mutateSession(session, `/api/sessions/${encodeURIComponent(session.id)}/archive`, "POST"),
    [mutateSession],
  );

  const restoreSession = useCallback(
    (session: SessionSummary) =>
      mutateSession(session, `/api/sessions/${encodeURIComponent(session.id)}/restore`, "POST"),
    [mutateSession],
  );

  const deleteSession = useCallback(
    (session: SessionSummary) =>
      mutateSession(session, `/api/sessions/${encodeURIComponent(session.id)}`, "DELETE", {
        confirm: "delete",
      }),
    [mutateSession],
  );

  const applyRunRecoveryResult = async (result: RunRecoveryResponse, sessionId: string) => {
    setRunRecoveryResult(result.state === "confirmation_required" ? result : null);
    setRunRecoveryNotice({
      tone:
        result.state === "failed"
          ? "warning"
          : result.state === "confirmation_required"
            ? "warning"
            : "success",
      message: recoveryResultMessage(result),
    });
    if (result.state !== "confirmation_required") {
      // 这些 chip 已经绑定到上一条 command，任何已收敛结果都禁止再次引用。
      updateSessionAttachments(sessionId, () => []);
    }
    await Promise.all([loadSnapshot(sessionId), loadSessions()]);
  };

  const checkUnknownRun = async () => {
    if (!selectedId || runRecoveryAction) return;
    setError("");
    setRunRecoveryNotice(null);
    if (!unknownRunId) {
      // 兼容早期 M3 客户端留下的 unknown 摘要：先重新加载 Snapshot 取 runId。
      await loadSnapshot(selectedId).catch((caught) =>
        setError(caught instanceof Error ? caught.message : "运行信息加载失败"),
      );
      return;
    }
    setRunRecoveryAction("checking");
    try {
      await applyRunRecoveryResult(await reconcileRun(selectedId, unknownRunId), selectedId);
    } catch (caught) {
      const message =
        caught instanceof ApiError
          ? `${caught.message}（错误 ID：${caught.errorId}）`
          : caught instanceof Error
            ? caught.message
            : "状态检查失败";
      setError(message);
    } finally {
      setRunRecoveryAction(null);
    }
  };

  const resolveUnknownRun = async (outcome: "completed" | "failed") => {
    if (!selectedId || !unknownRunId || runRecoveryAction) return;
    setError("");
    setRunRecoveryAction(outcome);
    try {
      await applyRunRecoveryResult(await resolveRun(selectedId, unknownRunId, outcome), selectedId);
    } catch (caught) {
      const message =
        caught instanceof ApiError
          ? `${caught.message}（错误 ID：${caught.errorId}）`
          : caught instanceof Error
            ? caught.message
            : "人工确认失败";
      setError(message);
    } finally {
      setRunRecoveryAction(null);
    }
  };

  const runControlFailure = (caught: unknown, fallback: string) =>
    caught instanceof ApiError
      ? `${caught.message}（错误 ID：${caught.errorId}）`
      : caught instanceof Error
        ? caught.message
        : fallback;

  const respondToApproval = async (
    action: Extract<RunAction, { type: "approval" }>,
    choice: ApprovalChoice,
  ) => {
    if (!selectedId || !runControl?.run || runControlBusy) return;
    if (
      choice === "always" &&
      !window.confirm("这会让 Hermes 今后始终允许同类操作。确认写入持久允许规则吗？")
    ) {
      return;
    }
    setRunControlBusy(action.id);
    setRunControlError("");
    try {
      const next = RunControlStateSchema.parse(
        await api<unknown>(
          `/api/sessions/${encodeURIComponent(selectedId)}/runs/${encodeURIComponent(runControl.run.id)}/actions/${encodeURIComponent(action.id)}/approval`,
          {
            method: "POST",
            body: JSON.stringify({
              choice,
              confirm: choice === "always" ? "persist_approval" : "respond_approval",
            }),
          },
        ),
      );
      if (selectedIdRef.current === selectedId) setRunControl(next);
      await loadSnapshot(selectedId);
    } catch (caught) {
      setRunControlError(runControlFailure(caught, "审批提交失败"));
      void loadRunControl(selectedId);
    } finally {
      setRunControlBusy(null);
    }
  };

  const respondToClarification = async (
    action: Extract<RunAction, { type: "clarification" }>,
    answer: string,
  ) => {
    if (!selectedId || !runControl?.run || runControlBusy) return;
    setRunControlBusy(action.id);
    setRunControlError("");
    try {
      const next = RunControlStateSchema.parse(
        await api<unknown>(
          `/api/sessions/${encodeURIComponent(selectedId)}/runs/${encodeURIComponent(runControl.run.id)}/actions/${encodeURIComponent(action.id)}/clarification`,
          {
            method: "POST",
            body: JSON.stringify({ answer, confirm: "respond_clarification" }),
          },
        ),
      );
      if (selectedIdRef.current === selectedId) setRunControl(next);
      await loadSnapshot(selectedId);
    } catch (caught) {
      setRunControlError(runControlFailure(caught, "回答提交失败"));
      void loadRunControl(selectedId);
    } finally {
      setRunControlBusy(null);
    }
  };

  const stopCurrentRun = async () => {
    if (!selectedId || !runControl?.run || runControlBusy) return;
    if (!window.confirm("确认停止当前运行吗？尚未执行的排队消息也会被清除。")) return;
    setRunControlBusy("stop");
    setRunControlError("");
    try {
      const next = RunControlStateSchema.parse(
        await api<unknown>(
          `/api/sessions/${encodeURIComponent(selectedId)}/runs/${encodeURIComponent(runControl.run.id)}/stop`,
          { method: "POST", body: JSON.stringify({ confirm: "stop_run" }) },
        ),
      );
      if (selectedIdRef.current === selectedId) setRunControl(next);
      await Promise.all([loadSnapshot(selectedId), loadSessions()]);
    } catch (caught) {
      setRunControlError(runControlFailure(caught, "停止请求失败"));
      void loadRunControl(selectedId);
    } finally {
      setRunControlBusy(null);
    }
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (
      !selectedId ||
      !text ||
      sending ||
      currentSessionArchived ||
      !attachmentsReady ||
      promptBlockedByRun
    )
      return;
    const targetSessionId = selectedId;
    const attachmentRefs = currentAttachments.map((attachment) => ({
      attachmentId: attachment.attachmentId!,
    }));
    setSending(true);
    shouldAutoScroll.current = true;
    setError("");
    try {
      await api("/api/prompts", {
        method: "POST",
        body: JSON.stringify({
          commandId: `cmd_${crypto.randomUUID()}`,
          sessionId: targetSessionId,
          text,
          attachments: attachmentRefs,
          clientRevision: snapshot?.revision ?? 0,
        }),
      });
      draftRef.current = "";
      setDraft("");
      localStorage.removeItem(`herskin:draft:${targetSessionId}`);
      // POST /api/prompts 明确成功后才清理；网络失败或结果未知时附件保持原状且不会自动重试。
      updateSessionAttachments(targetSessionId, () => []);
      setRunRecoveryNotice(null);
      await loadSnapshot(targetSessionId).catch((caught) => {
        setError(
          caught instanceof Error
            ? `消息已发送，但刷新会话失败：${caught.message}`
            : "消息已发送，但刷新会话失败",
        );
      });
    } catch (caught) {
      const baseMessage =
        caught instanceof ApiError
          ? `${caught.message}（错误 ID：${caught.errorId}）`
          : caught instanceof Error
            ? caught.message
            : "发送失败";
      const outcomeUnknown =
        caught instanceof ApiError &&
        (caught.code.includes("UNKNOWN") ||
          caught.code === "NETWORK_ERROR" ||
          caught.code === "HTTP_ERROR");
      const attachmentMessage = currentAttachments.length
        ? outcomeUnknown
          ? "发送结果待确认；原附件已锁定，不会自动重试"
          : "原附件已锁定且不能再次发送；请移除后重新添加"
        : "";
      if (currentAttachments.length) {
        updateSessionAttachments(targetSessionId, (attachments) =>
          lockAttachmentsAfterSubmitFailure(
            attachments,
            outcomeUnknown
              ? "与结果待确认的消息绑定，不能再次发送"
              : "提交未明确成功，不能再次发送；请移除后重新添加",
          ),
        );
      }
      setError(attachmentMessage ? `${baseMessage}。${attachmentMessage}` : baseMessage);
      await loadSnapshot(targetSessionId).catch(() => undefined);
    } finally {
      setSending(false);
    }
  };

  const logout = async () => {
    await api("/api/auth/logout", { method: "POST" });
    setAuth({ authenticated: false, user: null });
    setSessions([]);
    setSnapshot(null);
    setNotifications(null);
    setProfile(null);
    notificationBaselineReady.current = false;
    setAttachmentsBySession({});
  };

  if (loading)
    return (
      <main className="boot-screen">
        <div className="brand-mark">
          <Sparkles />
        </div>
        <p>正在启动 HerSkin…</p>
      </main>
    );
  if (!auth?.authenticated) return <LoginScreen onSuccess={setAuth} />;

  const browserOnline = realtimeState === "online";
  const hermesOnline = connection?.overall === "online";
  const isOnline = browserOnline && hermesOnline;
  const headerStatusLabel = !browserOnline
    ? "HerSkin 连接恢复中"
    : !hermesOnline
      ? "Hermes 连接恢复中"
      : displayedRunStatus
        ? statusLabel(displayedRunStatus)
        : "空闲";
  return (
    <div
      ref={appShellRef}
      className={`app-shell ${dockCollapsed ? "app-shell--dock-collapsed" : ""} ${activeWorkspace === "chat" && mobileChatDetail ? "app-shell--mobile-chat-detail" : ""}`}
    >
      <AppTopbar
        activeWorkspace={activeWorkspace}
        browserOnline={browserOnline}
        hermesOnline={hermesOnline}
        notificationsOpen={notificationsOpen}
        notifications={notifications}
        browserNotificationState={browserNotificationState}
        onToggleNotifications={toggleNotifications}
        onOpenNotification={openNotification}
        onEnableBrowserNotifications={() => void enableBrowserNotifications()}
        onToggleInspector={() => setInspectorOpen((value) => !value)}
        onLogout={() => void logout()}
      />

      {activeWorkspace === "chat" ? (
        <div
          className={`workspace ${inspectorOpen ? "workspace--with-inspector" : "workspace--without-inspector"} ${mobileChatDetail ? "workspace--mobile-detail" : "workspace--mobile-list"}`}
        >
          <SessionRail
            feedback={sessionFeedback}
            hermesProfiles={hermesProfiles}
            profileScope={profileScope}
            profilePickerRequest={profilePickerRequest}
            sessions={sessions}
            scopedSessions={scopedSessions}
            defaultAgentName={profile?.agent.displayName ?? "Hermes"}
            searchOpen={sessionSearchOpen}
            taskPushOpen={taskPushOpen}
            taskPushUnread={taskPushUnread}
            selectedId={selectedId}
            busySessionId={busySessionId}
            onToggleSearch={() => setSessionSearchOpen((value) => !value)}
            onCloseSearch={() => setSessionSearchOpen(false)}
            onCreateSession={() => {
              setSessionSearchOpen(false);
              void createSession();
            }}
            onOpenTaskPush={() => {
              persistDraftNow();
              setTaskPushOpen(true);
              setMobileChatDetail(true);
            }}
            onManageProfiles={() => {
              setMoreTarget({
                component: "hermes_serve",
                page: "profiles",
                nonce: Date.now(),
              });
              setActiveWorkspace("more");
            }}
            onSelectProfileScope={selectProfileScope}
            onSelectSession={selectSession}
            onRenameSession={renameSession}
            onArchiveSession={archiveSession}
            onRestoreSession={restoreSession}
            onDeleteSession={deleteSession}
          />

          {taskPushOpen ? (
            <TaskPushFeed
              onBack={() => setMobileChatDetail(false)}
              onUnreadCount={setTaskPushUnread}
              agentName={profile?.agent.displayName ?? "马思思"}
              agentAvatarUrl={profile?.agent.avatarUrl ?? null}
            />
          ) : (
            <main className="conversation-pane">
              <div className="conversation-header">
                <IconButton
                  className="mobile-only mobile-detail-back"
                  aria-label="返回会话列表"
                  onClick={() => setMobileChatDetail(false)}
                >
                  <ChevronLeft />
                </IconButton>
                <div>
                  <span className="section-kicker">Hermes 会话</span>
                  <div className="conversation-title-row">
                    <h1>{currentSession?.title ?? "新会话"}</h1>
                    {currentSession ? <small>Profile · {currentSession.profileName}</small> : null}
                  </div>
                  {currentSession ? (
                    <SessionIdentity
                      className="session-identity--full"
                      agentSessionId={currentSession.agentSessionId}
                    />
                  ) : null}
                </div>
                {currentSession ? (
                  <SessionIdentity
                    compact
                    className="session-identity--compact"
                    agentSessionId={currentSession.agentSessionId}
                  />
                ) : null}
                <StatusBadge
                  tone={isOnline ? runTone(displayedRunStatus) : "warning"}
                  isAnimated={!isOnline || isActivelyRunning(displayedRunStatus)}
                  showDot
                >
                  {headerStatusLabel}
                </StatusBadge>
              </div>

              <section
                className="message-scroll"
                aria-live="polite"
                ref={messageScroll}
                onScroll={(event) => {
                  const element = event.currentTarget;
                  shouldAutoScroll.current =
                    element.scrollHeight - element.scrollTop - element.clientHeight <= 64;
                }}
              >
                {!messages.length && !showRunPlaceholder && !runControl?.actions.length ? (
                  <div className="conversation-empty">
                    <div className="empty-orb">
                      <Sparkles />
                    </div>
                    <h2>和{currentAgentName}聊点什么</h2>
                    <p>
                      消息只会在按下发送后抵达服务器。可以随时切换会话，正在执行的任务不会被打断。
                    </p>
                  </div>
                ) : (
                  <div className="message-list">
                    {messageWindow.hiddenCount ? (
                      <button
                        type="button"
                        className="message-history-loader"
                        onClick={showEarlierMessages}
                      >
                        加载更早的消息
                        <small>还有 {messageWindow.hiddenCount} 条未挂载</small>
                      </button>
                    ) : null}
                    {messageWindow.visible.map(({ message, timeLabel, durationLabel }) => (
                      <MessageItem
                        key={message.id}
                        message={message}
                        timeLabel={timeLabel}
                        durationLabel={durationLabel}
                        profile={profile}
                        agentName={currentAgentName}
                      />
                    ))}
                    {runControl?.actions.length ? (
                      <RunActionCards
                        actions={runControl.actions}
                        busyActionId={runControlBusy}
                        mode="timeline"
                        onApproval={(action, choice) => void respondToApproval(action, choice)}
                        onClarification={(action, answer) =>
                          void respondToClarification(action, answer)
                        }
                      />
                    ) : null}
                    {showRunPlaceholder ? (
                      <RunPlaceholder
                        startedAt={snapshot?.activeRun?.startedAt ?? snapshot?.activeRun?.createdAt}
                        profile={profile}
                        agentName={currentAgentName}
                      />
                    ) : null}
                  </div>
                )}
              </section>

              <div className="composer-wrap">
                {snapshotLoadError ? (
                  <InlineError title="当前会话加载失败" message={snapshotLoadError} />
                ) : null}
                {error ? <InlineError title="操作没有完成" message={error} /> : null}
                {runRecoveryNotice ? (
                  <div
                    className={`run-recovery-notice run-recovery-notice--${runRecoveryNotice.tone}`}
                    role="status"
                  >
                    {runRecoveryNotice.message}
                  </div>
                ) : null}
                {displayedRunStatus === "unknown" ? (
                  <RunRecoveryCard
                    runId={unknownRunId}
                    result={runRecoveryResult}
                    busyAction={runRecoveryAction}
                    onCheck={() => void checkUnknownRun()}
                    onResolve={(outcome) => void resolveUnknownRun(outcome)}
                  />
                ) : null}
                <form className="composer" onSubmit={send}>
                  <AutoGrowTextarea
                    aria-label={`发送消息给${currentAgentName}`}
                    placeholder={`给${currentAgentName}发消息…`}
                    value={draft}
                    disabled={currentSessionArchived}
                    onChange={(event) => setDraft(event.target.value)}
                    onPaste={(event) => {
                      // 只有图片真正进入附件状态机时才拦截默认行为；普通文字粘贴完全不受影响。
                      if (stageClipboardImages(event.clipboardData, selectAttachments))
                        event.preventDefault();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        event.currentTarget.form?.requestSubmit();
                      }
                    }}
                  />
                  <AttachmentChips
                    attachments={currentAttachments}
                    disabled={sending || currentSessionArchived || promptBlockedByRun}
                    onRemove={(attachment) => void removeAttachment(attachment)}
                  />
                  <div className="composer-footer">
                    <div className="composer-left-actions">
                      <AttachmentPicker
                        count={currentAttachments.length}
                        disabled={
                          !selectedId || currentSessionArchived || sending || promptBlockedByRun
                        }
                        onFilesSelected={selectAttachments}
                      />
                      <button
                        type="button"
                        className="context-usage"
                        title={contextCopy}
                        aria-label={`查看上下文用量：${contextLabel}`}
                        onClick={() => setInspectorOpen(true)}
                      >
                        <span>上下文</span>
                        <strong className="context-label-long">{contextLabel}</strong>
                        <strong className="context-label-short">{mobileContextLabel}</strong>
                      </button>
                    </div>
                    <div className="composer-actions">
                      <ModelSelector />
                      <ComposerSendButton
                        sending={sending}
                        disabled={
                          !selectedId ||
                          !draft.trim() ||
                          currentSessionArchived ||
                          !attachmentsReady ||
                          promptBlockedByRun
                        }
                      />
                    </div>
                  </div>
                </form>
                <p className="composer-hint">
                  {currentSessionArchived
                    ? "已归档会话为只读，恢复后可继续对话"
                    : currentPromptBlockedHint
                      ? currentPromptBlockedHint
                      : !attachmentsReady
                        ? "请等待附件上传完成，或移除失败的附件"
                        : "Enter 发送 · Shift + Enter 换行 · 输入不会触发网络通信"}
                </p>
              </div>
            </main>
          )}

          <ChatInspector
            visible={inspectorOpen && !taskPushOpen}
            displayedRunStatus={displayedRunStatus}
            runControl={runControl}
            runControlBusy={runControlBusy}
            runControlError={runControlError}
            connection={connection}
            runtimeState={runtimeState}
            contextLabel={contextLabel}
            contextCopy={contextCopy}
            onClose={() => setInspectorOpen(false)}
            onApproval={(action, choice) => void respondToApproval(action, choice)}
            onClarification={(action, answer) => void respondToClarification(action, answer)}
            onStop={() => void stopCurrentRun()}
          />
        </div>
      ) : activeWorkspace === "overview" ? (
        <Suspense fallback={<WorkspaceSkeleton label="运行概览" />}>
          <ManagementWorkspace mode="overview" />
        </Suspense>
      ) : activeWorkspace === "memory" ? (
        <main className="management-content management-content--standalone management-content--memory">
          <Suspense fallback={<WorkspaceSkeleton label="Memory 编辑器" kind="editor" />}>
            <MemoryWorkspace onDirtyChange={setMemoryDirty} />
          </Suspense>
        </main>
      ) : activeWorkspace === "cron" ? (
        <Suspense fallback={<WorkspaceSkeleton label="任务工作区" kind="list" />}>
          <CronWorkspace
            focusJobId={cronFocusId}
            onOpenLogs={(job) => {
              setMoreTarget({
                component: job.level === "channel" ? "hermes_gateway" : "hermes_serve",
                nonce: Date.now(),
              });
              setActiveWorkspace("more");
            }}
          />
        </Suspense>
      ) : activeWorkspace === "files" ? (
        <Suspense fallback={<WorkspaceSkeleton label="文件工作区" kind="files" />}>
          <FileWorkspace />
        </Suspense>
      ) : activeWorkspace === "more" ? (
        <Suspense fallback={<WorkspaceSkeleton label="管理工作区" />}>
          <ManagementWorkspace
            key={moreTarget?.nonce ?? "management-default"}
            initialPage={moreTarget?.page ?? (moreTarget ? "connection" : "home")}
            initialComponent={moreTarget?.component ?? "hermes_serve"}
          />
        </Suspense>
      ) : (
        <main className="workspace-coming-soon" />
      )}

      <WorkspaceDock
        activeWorkspace={activeWorkspace}
        collapsed={dockCollapsed}
        onCollapsedChange={setDockCollapsed}
        onIntent={(workspace) => {
          const pending = preloadWorkspace(workspace);
          // 预加载失败不打断当前页面；真正导航时 lazy loader 会自动重试并交由错误边界处理。
          if (pending) void pending.catch(() => undefined);
        }}
        onSelect={(workspace) => {
          if (
            activeWorkspace === "memory" &&
            workspace !== "memory" &&
            memoryDirty &&
            !window.confirm("记忆文档还有未保存草稿。离开后草稿会丢失，是否继续？")
          ) {
            return false;
          }
          if (workspace === "more") setMoreTarget(null);
          if (workspace === "chat") setMobileChatDetail(false);
          setActiveWorkspace(workspace);
          return true;
        }}
      />
    </div>
  );
}
